
import moment from 'moment'
import getProp from '@f/get-prop'

/**
 * Check if workspace is an invoiced customer (business or team-$0) and has a MTU quota set
 * As we add new invoiced plans in stripe, this method needs updating
 *
 * @param {Object} workspace
 * @return {Boolean}
 * @api public
 */

export function isWorkspaceInvoiced (billing) {
  const plan = getProp('subscription.plan.id', billing)
  return plan === 'business' || plan === 'team-$0'
}

/**
 * Check if workspace is an invoiced customer (business or team-$0) and has a MTU quota set
 * As we add new invoiced plans in stripe, this method needs updating
 *
 * @param {Object} workspace
 * @return {Boolean}
 * @api public
 */

export function isWorkspaceInvoicedAndWithQuota (billing) {
  const plan = getProp('subscription.plan.id', billing)

  // invoiced workspaces are any `business` plans or `team-$0` plans
  const isInvoicedWorkspace = plan === 'business' || plan === 'team-$0'

  // workspaces have a MTU quota if a CSM sets it via slack
  const trackedObjects = getProp('quota.trackedObjects', billing)

  return isInvoicedWorkspace && !!trackedObjects
}

/**
 * Check if the given `workspace` is on a "grandfathered" plan.
 *
 * @param {Object} workspace
 * @return {Boolean}
 * @api public
 */

export function isGrandfathered (workspace) {
  return getProp('billing.legacy', workspace) || false
}

/**
 * Get the "plan" from the given `workspace`.
 *
 * @param {Object} workspace
 * @return {Object}
 * @api public
 */

export function getPlan (workspace) {
  if (isGrandfathered(workspace)) {
    let plan = getProp('billing.services.integrations.subscription.plan', workspace)
    if (!plan) {
      plan = getProp('billing.services.warehouses.subscription.plan', workspace)
    }
    return plan
  }
  return getProp('billing.subscription.plan', workspace) || false
}

/**
 * Check if the given `workspace` is on a "business" teir.
 *
 * @param {Object} workspace
 * @return {Boolean}
 * @api public
 */

export function isBusinessTier (workspace) {
  if (isGrandfathered(workspace)) {
    let tier = getProp('billing.services.integrations.subscription.plan.tier', workspace)
    if (!tier) {
      tier = getProp('billing.services.warehouses.subscription.plan.tier', workspace)
    }
    return tier === 'business'
  }
  return getProp('billing.subscription.plan.id', workspace) === 'business'
}

/**
 * Check if the given `workspace` is on a trial.
 *
 * @param {Object} workspace
 * @return {Boolean}
 * @api public
 */

export function isOnTrial (workspace) {
  if (!workspace.billing) return false

  if (isGrandfathered(workspace)) {
    const trialEnd = getTrialEnd(workspace)
    return new Date() < trialEnd
  }

  // TODO(stephenmathieson): validate this is correct
  return workspace.billing.subscription.trial
}

/**
 * Get the trial end date for the given `workspace`.
 *
 * @param {Object} workspace
 * @return {Date}
 * @api public
 */

export function getTrialEnd (workspace) {
  if (isGrandfathered(workspace)) {
    let trialEnd = getProp('billing.services.integrations.subscription.trialEnd', workspace)
    if (!trialEnd) {
      trialEnd = getProp('billing.services.warehouses.subscription.trialEnd', workspace)
    }
    return new Date(trialEnd)
  }

  return new Date(workspace.billing.subscription.trialEnd)
}

/**
 * Check if workspace is using more integrations than their plan allows
 * @param {Object} workspace
 * @return {Boolean}
 */

export function calculateExceededIntegrations (service, usage) {
  const { quota } = service
  const arr = []

  if (usage.levels) {
    Object.keys(usage.levels).forEach(function (level) {
      usage.levels[level].forEach(function (name) {
        if (level <= quota.level) return
        if (quota.exceptions && quota.exceptions[name]) return
        arr.push(name)
      })
    })
  }

  return arr.length ? arr : false
}

/**
 * Check if workspace is exceeding their monthly quota calls
 * @param {Object} workspace
 * @return {Boolean}
 */

export function hasExceededLegacyQuota (workspace) {
  if (!isGrandfathered(workspace)) return false // MTU plans don't have usage quotas
  const allowedCalls = workspace.billing.services.integrations.quota.calls
  const actualCalls = workspace.billing.services.integrations.usage.calls
  const date = workspace.billing.services.integrations.subscription.periodStart
  const daysSinceLastBill = moment.duration(moment(new Date()) - moment(date)).asDays()
  const callsPerDay = actualCalls / Math.ceil(daysSinceLastBill)
  const projectedCalls = callsPerDay * 30
  return allowedCalls <= projectedCalls
}

/**
 * Check if we've had issues charging a credit card for the given workspace
 * @param {Object} workspace
 * @return {Boolean}
 */

export function hasPastDuePayment (workspace) {
  let result

  if (isGrandfathered(workspace)) {
    let status = getProp('billing.services.integrations.subscription.status', workspace)
    if (!status) {
      status = getProp('billing.services.warehouses.subscription.status', workspace)
    }
    result = status === 'past_due'
  } else {
    result = getProp('billing.subscription.status', workspace) === 'past_due'
  }

  return result
}



// WEBPACK FOOTER //
// ./lib/utils/billing.js